// src/components/Header.js
import React, { useContext, useState, useEffect, useRef } from 'react';
import { AppBar, Toolbar, Box, IconButton, Typography, Menu, MenuItem, useMediaQuery } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import { AuthContext } from '../context/AuthContext';
import SearchBar from './SearchBar';
import NotificationDropdown from './NotificationDropdown';
import api from '../services/api';
import wmeterLogo from '../assets/wmeter-logo.png';
import { Link, useNavigate } from 'react-router-dom';

const Header = () => {
  const { auth, logout } = useContext(AuthContext);
  const [language, setLanguage] = useState('en');
  const [uptime, setUptime] = useState('');
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);

  // Apply special layout for width ≤400px OR height ≤500px
  const isXs = useMediaQuery(
    '(max-width:400px), (max-height:550px)',
    { noSsr: true }
  );
  const isMobile = useMediaQuery('(max-width:1000px)');
  const navigate = useNavigate();
  const isMobileMenuOpen = Boolean(mobileMenuAnchorEl);

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'fr' : 'en';
    setLanguage(newLang);
    window.location.reload();
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const formatUptime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    // Once a full day is reached, switch to a day-based format.
    if (hrs >= 24) {
      const days = Math.floor(hrs / 24);
      return `${days} d, ${hrs % 24} hr, ${mins} min, ${secs} sec`;
    }

    return `${hrs} hr, ${mins} min, ${secs} sec`;
  };

  // AbortController ref
  const uptimeAbortControllerRef = useRef();

  const fetchUptime = () => {
    // abort previous if any
    uptimeAbortControllerRef.current?.abort();
    const controller = new AbortController();
    uptimeAbortControllerRef.current = controller;

    api.get('/api/proxy/', { signal: controller.signal })
      .then((res) => {
        const rawUptime = parseFloat(res.data.uptime);
        setUptime(formatUptime(rawUptime));
      })
      .catch((err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
        }
      });
  };

  useEffect(() => {
    fetchUptime();
    const interval = setInterval(fetchUptime, 5000);
    return () => {
      clearInterval(interval);
      uptimeAbortControllerRef.current?.abort();
    };
  }, []);

  const mobileMenu = (
    <Menu
      anchorEl={mobileMenuAnchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      {/* Navigation links */}
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/'); }}>Overview</MenuItem>
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/gateways'); }}>Gateways</MenuItem>
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/lost-gateways'); }}>Lost Gateways</MenuItem>
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/water-meters'); }}>Water Meters</MenuItem>
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/lost-water-meters'); }}>Lost Water Meters</MenuItem>
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/down-water-meters'); }}>Down Water Meters</MenuItem>
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/criticals'); }}>Criticals</MenuItem>
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/water-meter-registration'); }}>Water Meter Registration</MenuItem>
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/gateway-registration'); }}>Gateway Registration</MenuItem>

      {/* XS-only menu items */}
      {isXs && (
        <>
          <MenuItem onClick={() => { handleMobileMenuClose(); toggleLanguage(); }}>Switch Language</MenuItem>
          <MenuItem onClick={() => { handleMobileMenuClose(); logout(); }}>Logout</MenuItem>
        </>
      )}
    </Menu>
  );

  return (
    <Box>
      {/* Top mini header, hidden on XS */}
      {!isXs && (
        <AppBar position="static" sx={{ backgroundColor: '#2c3e50', p: '4px' }}>
          <Toolbar variant="dense" sx={{ justifyContent: 'flex-end' }}>
            <IconButton color="inherit" onClick={toggleLanguage}>
              <LanguageIcon />
            </IconButton>
            <IconButton color="inherit">
              <AccountCircleIcon />
            </IconButton>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {auth.username || 'User'}
            </Typography>
            <IconButton color="inherit" onClick={logout}>
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* Main header */}
      <AppBar position="static" sx={{ backgroundColor: '#34495e', p: '8px' }}>
        <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          {isXs ? (
            <>  {/* XS or low-height view: burger, title, notification */}
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                onClick={handleMobileMenuOpen}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Typography variant="h3" sx={{ flexGrow: 1, textAlign: 'center' }}>
                  w-meter
                </Typography>
              </Link>
              <NotificationDropdown />
            </>
          ) : isMobile ? (
            <>  {/* Mobile view (≤1000px but >400px height) */}
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                onClick={handleMobileMenuOpen}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexGrow: 1 }}>
                <Link to="/" style={{ textDecoration: 'none' }}>
                  <img src={wmeterLogo} alt="w-meter Dashboard Logo" style={{ height: '50px', marginBottom: 0 }} />
                </Link>
              </Box>
              <NotificationDropdown />
            </>
          ) : (
            <>  {/* Desktop view */}
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 220 }}
              >
                <Link to="/" style={{ textDecoration: 'none' }}>
                  <img src={wmeterLogo} alt="w-meter Dashboard Logo" style={{ height: '50px', marginBottom: 0 }} />
                </Link>
                <Typography variant="caption" sx={{ minWidth: '150px' }}>
                  Server uptime: {uptime}
                </Typography>
              </Box>
              <SearchBar />
              <NotificationDropdown />
            </>
          )}
        </Toolbar>

        {/* Search row */}
        {(isXs || isMobile) && (
          <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'center' }}>
            <SearchBar />
          </Box>
        )}
      </AppBar>

      {mobileMenu}
    </Box>
  );
};

export default Header;
